import { default as React, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { CloudAccountType } from './types/CloudAccountTypes';
import { GetCloudAccountsAndRolesForApplication } from './cloudAccounts/CloudAccountQueries';
import * as XLSX from 'xlsx';
import { CNAPCGAAudit, Device } from './types/InsightsTypes';
import {
  applicationCloudAccountQueryUrl,
  applicationDeviceQueryUrl,
  GetDevicesQuery,
} from './queries/InsightsQueries';
import {
  asExportableCnapData,
  asExportableDevicesForAudit,
  escapeCSVValue,
} from '../../util/export';
import { fetchCnapData } from '../../util/cnap';

import { AxiosError } from 'axios';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  DevHubButton,
  DevHubCircularProgress,
  DevHubDynamicIcon,
  DevHubGrid,
  DevHubMenuItem,
  DevHubSelect,
  DevHubTooltip,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';

interface ExportResourcesReportProps {
  appId: number;
  handleAlert: (alert: {
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }) => void;
}

enum FILETYPE {
  CSV = 'csv',
  EXCEL = 'excel',
}

interface CloudAccountExportType {
  'Account name': string;
  'Account ID': string;
  Platform: string;
  Environment: string;
  'Technical owner': string;
  'Associated applications': string;
}
/** enum for device types */
export enum DeviceType {
  ChassisServer = 'CHASSIS_SERVER',
  Daas = 'DAAS',
  Database = 'DATABASE',
  FddiNetwork = 'FDDI_NETWORK',
  Msd = 'MSD',
  Netgear = 'NETGEAR',
  PhysicalServer = 'PHYSICAL_SERVER',
  Unknown = 'UNKNOWN',
  VirtualMachine = 'VIRTUAL_MACHINE',
}

/**
 * Component for exporting application reports.
 * @param ExportResourcesReportProps props - The properties for the component.
 */
const ExportResourcesReport = ({
  appId,
  handleAlert,
}: ExportResourcesReportProps) => {
  const [deviceExportData, setDeviceExportData] = useState<Device[]>(
    [] as Device[],
  );
  const [cloudExportData, setCloudExportData] = useState<
    CloudAccountExportType[]
  >([]);
  const [cnapExportData, setCnapExportData] = useState<CNAPCGAAudit[]>(
    [] as CNAPCGAAudit[],
  );

  const config = useApi(configApiRef);
  const cnapCGAAuditUrl = config.getString('app.cnapCGAAuditUrl');

  const [cnapError, setCnapError] = useState<any>(null);
  const [downloadError, setDownloadError] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState(false);
  const [deviceLoading, setDeviceLoading] = useState(false);
  const [resourceDownloading, setResourceDownloading] = useState(false);

  const [deviceReady, setDeviceReady] = useState(false);
  const [cloudReady, setCloudReady] = useState(false);
  const [cnapReady, setCnapReady] = useState(false);

  const [fileType, setFileType] = useState(FILETYPE.CSV);

  const [fetchCloudAccounts, { error: cloudError, loading: cloudLoading }] =
    useLazyQuery(GetCloudAccountsAndRolesForApplication, {
      fetchPolicy: 'cache-and-network',
    });

  const [fetchDevices, { error: deviceError, fetchMore }] = useLazyQuery(
    GetDevicesQuery,
    {
      fetchPolicy: 'cache-and-network',
    },
  );

  const handleCloudAccountDownload = async () => {
    if (!appId) return;

    const result = await fetchCloudAccounts({
      variables: { applicationId: appId },
    });

    const accounts: CloudAccountType[] =
      result.data?.resources?.application?.cloudAccounts || [];

    const isReady =
      result.data?.resources?.application?.applicationId === appId &&
      accounts.length >= 0;

    const formattedCloudData = accounts.map(cloudAccount => ({
      'Account name': cloudAccount.name,
      'Account ID': cloudAccount.id,
      Platform: cloudAccount.platform,
      Environment: cloudAccount.environment,
      'Technical owner':
        result.data?.resources?.application?.owner.displayName ?? '',
      'Associated applications':
        result.data?.resources?.application?.name ?? '',
    }));

    setCloudExportData(formattedCloudData);
    setCloudReady(isReady);
  };

  const handleDeviceDownload = async () => {
    if (!appId) return;

    const result = await fetchDevices({
      variables: {
        applicationId: appId,
        pagination: { limit: 100, offset: 0 },
      },
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    });

    const devicesPaged = result.data?.resources?.application?.devicesPaged;
    const totalCount = devicesPaged?.totalCount ?? 0;
    const firstPageDevices = devicesPaged?.devices ?? [];

    if (!totalCount || totalCount === 0) {
      setDeviceExportData([]);
      setDeviceLoading(false);
      setDeviceReady(true);
      return;
    }

    if (
      deviceExportData?.length === totalCount &&
      deviceExportData.length > 0
    ) {
      setDeviceLoading(false);
      setDeviceReady(true);
      return;
    }

    if (totalCount <= 100) {
      setDeviceExportData(firstPageDevices);
      setDeviceReady(true);
      return;
    }

    const offsets = [];
    for (let i = 100; i < totalCount; i += 100) {
      offsets.push(i);
    }

    try {
      const results = await Promise.all(
        offsets.map(offset =>
          fetchMore({
            variables: { pagination: { limit: 100, offset } },
            updateQuery: (prev, { fetchMoreResult }) => fetchMoreResult || prev,
          }),
        ),
      );

      const moreDevices = results.flatMap(
        moreData =>
          moreData.data?.resources?.application?.devicesPaged?.devices ?? [],
      );

      setDeviceExportData([...firstPageDevices, ...moreDevices]);
      setDeviceReady(true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error fetching devices:', err);
    }
  };

  const handleCNAPDownload = async () => {
    if (!appId) return;

    try {
      const items = await fetchCnapData(config, appId);

      if (!items || items.length === 0) {
        setCnapExportData([] as CNAPCGAAudit[]);
        setCnapReady(true);
        return;
      }

      setCnapExportData(items);
      setCnapReady(true);
    } catch (err: AxiosError | any) {
      if (err?.response?.status === 404) {
        setCnapExportData([] as CNAPCGAAudit[]);
        setCnapReady(true);
        return;
      }

      setCnapError(err);
    }
  };

  const handleCSVDownload = async () => {
    const physicalServers = deviceExportData.filter(
      d => d.type === DeviceType.PhysicalServer,
    );
    const virtualMachines = deviceExportData.filter(
      d => d.type === DeviceType.VirtualMachine,
    );

    const generateDeviceCSVData = (devices: Device[], sectionName: string) => {
      const exportableDevices = asExportableDevicesForAudit(devices);
      const headers =
        exportableDevices.length > 0 ? Object.keys(exportableDevices[0]) : [];
      return [
        [sectionName],
        ...(devices.length > 0
          ? [
              headers,
              ...exportableDevices.map(device =>
                headers.map(header => escapeCSVValue(device[header])),
              ),
            ]
          : [['', 'no data available']]),
        [],
      ];
    };

    const csvData = [
      ['Compliance Report'],
      ...generateDeviceCSVData(physicalServers, 'OnPrem Server'),
      ...generateDeviceCSVData(virtualMachines, 'Private Cloud Server'),
      ['Cloud Accounts'],
      ...(cloudExportData && cloudExportData.length > 0
        ? [
            ['Account name', 'Account ID', 'Platform', 'Environment'],
            ...cloudExportData.map(account => [
              account['Account name'],
              account['Account ID'],
              account.Platform,
              account.Environment,
            ]),
          ]
        : [['', 'no data available']]),
      [],
      ['CNAP Report'],
      ...(cnapExportData && cnapExportData.length > 0
        ? [
            [
              'DevHub App Name',
              'Application ID',
              'App Route',
              'App Name',
              'Subnet',
              'Platform',
            ],
            ...cnapExportData.map(cnap => [
              cnap.devhubAppName,
              cnap.applicationId,
              cnap.appRoute,
              cnap.appName,
              cnap.subnet,
              cnap.platform,
            ]),
          ]
        : [['', 'no data available']]),
      [],
      ['Links to fetch data'],
      ['Query name', 'Url'],
      ['Cloud Accounts query', applicationCloudAccountQueryUrl],
      ['Devices query', applicationDeviceQueryUrl],
      ['CNAP Report API', `${cnapCGAAuditUrl}/${appId}`],
      [],
    ];
    const csvContent = `data:text/csv;charset=utf-8,${csvData
      .map(e => e.join(','))
      .join('\n')}`;
    const encodedUri = encodeURI(csvContent);

    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Compliance_Report_${appId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsLoading(false);
    setDeviceReady(false);
    setCloudReady(false);
    setCnapReady(false);
  };

  const addSheetWithQuery = (
    workbook: XLSX.WorkBook,
    data: any[],
    sheetName: string,
    queryLabel: string,
    queryUrl: string,
    exportFn?: (data: any[]) => any[],
  ) => {
    const exportData = exportFn ? exportFn(data) : data;
    const columnCount =
      exportData.length > 0 ? Object.keys(exportData[0]).length : 0;
    const EXCEL_COLUMN_WIDTHS = Array(columnCount).fill({ wch: 20 });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    worksheet['!cols'] = EXCEL_COLUMN_WIDTHS;
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.utils.sheet_add_aoa(worksheet, [[queryLabel, queryUrl]], {
      origin: exportData?.length + 2,
    });
  };

  const handleExportExcel = async () => {
    const workbook = XLSX.utils.book_new();

    workbook.Props = {
      Title: `Compliance Report for Application ID ${appId}`,
      Subject: 'Compliance Report',
    };
    const physicalServers = deviceExportData.filter(
      d => d.type === DeviceType.PhysicalServer,
    );
    const virtualMachines = deviceExportData.filter(
      d => d.type === DeviceType.VirtualMachine,
    );

    const addDeviceSheet = (devices: Device[], sheetName: string) => {
      const data =
        devices && devices.length > 0
          ? asExportableDevicesForAudit(devices)
          : [{ [sheetName]: 'No data available' }];
      const worksheet = XLSX.utils.json_to_sheet(data);
      worksheet['!cols'] = Array(7).fill({ wch: 20 });
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
      XLSX.utils.sheet_add_aoa(
        worksheet,
        [['Devices query', applicationDeviceQueryUrl]],
        { origin: data.length + 2 },
      );
    };

    // Devices worksheet
    addDeviceSheet(physicalServers, 'OnPrem Server');
    addDeviceSheet(virtualMachines, 'Private Cloud Server');

    // Cloud accounts worksheet
    addSheetWithQuery(
      workbook,
      cloudExportData.length > 0
        ? cloudExportData
        : [{ 'Cloud Accounts': 'No data available' }],
      'Cloud Accounts',
      'Cloud Accounts query',
      applicationCloudAccountQueryUrl,
    );

    // CNAP Report Sheet
    addSheetWithQuery(
      workbook,
      cnapExportData.length > 0
        ? asExportableCnapData(cnapExportData)
        : [{ 'CNAP Report': 'No data available' }],
      'CNAP Report',
      'CNAP Report API',
      `${cnapCGAAuditUrl}/${appId}`,
    );

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });
    const excelBlob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const excelUrl = URL.createObjectURL(excelBlob);
    const a = document.createElement('a');
    a.href = excelUrl;
    a.download = `Compliance_Report_${appId}.xlsx`;
    a.click();
    URL.revokeObjectURL(excelUrl);
    setIsLoading(false);
    setDeviceReady(false);
    setCloudReady(false);
    setCnapReady(false);
  };

  useEffect(() => {
    if (deviceError || cloudError || cnapError) {
      // eslint-disable-next-line no-console
      console.error(
        'Error fetching data:',
        deviceError || cloudError || cnapError,
      );
    }
    if (deviceLoading || cloudLoading) {
      return;
    }

    const ready = deviceReady && cloudReady && cnapReady;

    if (!ready) return;

    if (fileType === FILETYPE.CSV) {
      handleCSVDownload();
    }
    if (fileType === FILETYPE.EXCEL) {
      handleExportExcel();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deviceExportData,
    cloudExportData,
    cnapExportData,
    fileType,
    deviceReady,
    cloudReady,
    cnapReady,
  ]);

  const [format, setFormat] = useState('csv');

  const handleClick = async (typeOfFile: string) => {
    setIsLoading(true);
    setFileType(typeOfFile as FILETYPE);
    handleDeviceDownload();
    handleCloudAccountDownload();
    handleCNAPDownload();
  };

  useEffect(() => {
    if (isLoading && !deviceReady && !cloudReady && !cnapReady) {
      setResourceDownloading(true);
      handleAlert({
        open: true,
        message: 'Exporting data. Please wait...',
        severity: 'info',
      });
    }
    if (deviceReady && cloudReady && cnapReady) {
      setResourceDownloading(false);
      handleAlert({
        open: true,
        message: 'Export completed successfully.',
        severity: 'success',
      });
    }
    if (deviceError || cloudError || cnapError) {
      setDownloadError(true);
      handleAlert({
        open: true,
        message: 'Error occurred while exporting. Please try again',
        severity: 'error',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    deviceReady,
    cloudReady,
    cnapReady,
    deviceError,
    cloudError,
    cnapError,
  ]);

  return (
    <React.Fragment>
      {resourceDownloading ? (
        <DevHubCircularProgress />
      ) : (
        <React.Fragment>
          {downloadError ? (
            <DevHubTooltip title="Something went wrong. Please try again after refreshing the page.">
              <span>
                <DevHubDynamicIcon
                  style={{ marginLeft: '0.5rem' }}
                  iconName="ReportProblem"
                  color="error"
                  fontSize="large"
                />
              </span>
            </DevHubTooltip>
          ) : (
            <DevHubGrid
              container
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <DevHubGrid item>
                <DevHubTypography variant="body1">Format</DevHubTypography>
              </DevHubGrid>
              <DevHubGrid item>
                <DevHubSelect
                  value={format}
                  onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
                    setFormat(e.target.value as string);
                  }}
                >
                  <DevHubMenuItem value="csv">CSV</DevHubMenuItem>
                  <DevHubMenuItem value="excel">Excel</DevHubMenuItem>
                </DevHubSelect>
              </DevHubGrid>
              <DevHubGrid item>
                <DevHubButton
                  variant="contained"
                  color="primary"
                  onClick={() => handleClick(format)}
                  data-gtm-event="export-resource-report"
                >
                  Export
                </DevHubButton>
              </DevHubGrid>
            </DevHubGrid>
          )}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default ExportResourcesReport;
